import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { COPY } from '../../data/copy';

const FALLBACK = COPY.hero.proofFallback;

function easeOutExpo(t: number): number {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
}

function useAnimatedCount(target: number, duration = 1800): number {
  const [count, setCount] = useState(0);
  const rafRef = useRef<number>(0);
  const startRef = useRef<number>(0);

  useEffect(() => {
    if (target === 0) return;
    startRef.current = performance.now();

    function tick(now: number) {
      const elapsed = now - startRef.current;
      const progress = Math.min(elapsed / duration, 1);
      setCount(Math.round(easeOutExpo(progress) * target));
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);

  return count;
}

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
  return n.toLocaleString('pt-BR');
}

export function ProofCounter() {
  const [total, setTotal] = useState(0);

  useEffect(() => {
    supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true })
      .then(
        ({ count }) => { setTotal(count && count > FALLBACK ? count : FALLBACK); },
        () => { setTotal(FALLBACK); },
      );
  }, []);

  const animated = useAnimatedCount(total);

  if (total === 0) return null;

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.35rem 0.85rem',
        background: 'rgba(212,175,55,0.08)',
        border: '1px solid rgba(212,175,55,0.25)',
        borderRadius: 'var(--radius-pill)',
        marginBottom: '1.25rem',
      }}
    >
      <span
        aria-hidden="true"
        style={{ fontSize: '0.75rem', color: '#D4AF37', lineHeight: 1 }}
      >
        ✦
      </span>
      <span
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-13)',
          fontWeight: 700,
          color: '#D4AF37',
          letterSpacing: '0.01em',
        }}
      >
        Mais de{' '}
        <span
          aria-live="polite"
          aria-label={`${total.toLocaleString('pt-BR')} ${COPY.hero.proofLabel}`}
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {formatCount(animated)}
        </span>{' '}
        {COPY.hero.proofLabel}
      </span>
    </div>
  );
}
